import BrowseGalleryIcon from "@mui/icons-material/BrowseGallery";
import CategoryIcon from "@mui/icons-material/Category";
import VisibilityOffOutlined from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { useExcludedPeriods } from "components/manualReview/contexts/ExcludedPeriodsContext";
import { useMaterialExclusion } from "components/manualReview/contexts/MaterialExclusionContext";
import { useTimingExclusion } from "components/manualReview/contexts/TimingExclusionContext";
import { ScrapEyesEventWithReviewData } from "components/manualReview/contexts/ManualReviewEventContext";
import { useTimelineZoom } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/TimelineZoomContext";
import dayjs, { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo, useRef } from "react";
import { LabellerTimeline } from "src/store/api/generatedApi";
import { LabellerTimelineRow } from "./components/LabellerTimelineRow";
import { LiveCursorLine } from "components/scrapEyeDataDashboard/VideoPlayer/components/LiveCursorLine";
import { useTimelinesByLabeller } from "./hooks/useTimelinesByLabeller";

interface LabellerVideoTimelinesProps {
  mergedEvents: ScrapEyesEventWithReviewData[];
  location: string | undefined;
  videoStart: Dayjs;
  currentTime: number;
  duration: number;
  labellerTimelines: LabellerTimeline[];
}

export const LabellerVideoTimelines = ({
  mergedEvents,
  location,
  labellerTimelines,
  videoStart,
  currentTime,
  duration,
}: LabellerVideoTimelinesProps) => {
  const zoomContext = useTimelineZoom();

  const visibleStart = zoomContext?.visibleStart ?? videoStart;
  const visibleDuration = zoomContext?.duration ?? duration;

  const { t } = useTenantTranslation();
  const { areAllExcluded, togglePeriods, exclusionsLocked } =
    useExcludedPeriods();
  const { areAllMaterialExcluded, toggleMaterialExclusion } =
    useMaterialExclusion();
  const { areAllTimingExcluded, toggleTimingExclusion } = useTimingExclusion();
  const timelinesInWindow = useMemo(() => {
    const videoEnd = videoStart.add(duration, "second");
    return labellerTimelines.filter((timeline) =>
      timeline.periods.some(
        (period) =>
          dayjs(period.end).isAfter(videoStart) &&
          dayjs(period.start).isBefore(videoEnd)
      )
    );
  }, [labellerTimelines, videoStart, duration]);
  const lanesInWindow = useTimelinesByLabeller(
    timelinesInWindow,
    mergedEvents,
    location
  );

  const trackRef = useRef<HTMLDivElement>(null);

  const laneKey = (lane: {
    labellerDetails: { id: string; labeller_type: string };
  }) => `${lane.labellerDetails.labeller_type}:${lane.labellerDetails.id}`;

  const lanePeriodIds = useMemo(
    () =>
      new Map(
        lanesInWindow.map((lane) => [
          laneKey(lane),
          lane.periods.flatMap((p) => p.periodIds),
        ])
      ),
    [lanesInWindow]
  );

  if (lanesInWindow.length === 0) {
    return null;
  }

  return (
    <Box ref={trackRef} sx={{ position: "relative" }}>
      <LiveCursorLine
        videoStart={videoStart}
        visibleStart={visibleStart}
        visibleDuration={visibleDuration}
        trackRef={trackRef}
      />

      <Stack spacing={1}>
        {lanesInWindow.map((lane) => {
          const allPeriodIds = lanePeriodIds.get(laneKey(lane)) ?? [];
          const allExcluded = areAllExcluded(allPeriodIds);
          const allMaterialExcluded = areAllMaterialExcluded(allPeriodIds);
          const allTimingExcluded = areAllTimingExcluded(allPeriodIds);

          const lockedTitle = t("labellerExclusionsLocked");

          const visibilityButton = (
            <Tooltip
              title={
                exclusionsLocked
                  ? lockedTitle
                  : t(allExcluded ? "includeLabeller" : "excludeLabeller")
              }
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => {
                    togglePeriods(allPeriodIds);
                  }}
                  disabled={exclusionsLocked}
                  aria-label={t(
                    allExcluded ? "includeLabeller" : "excludeLabeller"
                  )}
                  sx={{ p: 0.25 }}
                >
                  {allExcluded ? (
                    <VisibilityOffOutlined sx={{ fontSize: "1.2rem" }} />
                  ) : (
                    <VisibilityOutlined sx={{ fontSize: "1.2rem" }} />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          );

          const endAdornment = (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip
                title={
                  exclusionsLocked
                    ? lockedTitle
                    : t(allTimingExcluded ? "includeTiming" : "excludeTiming")
                }
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      toggleTimingExclusion(allPeriodIds);
                    }}
                    disabled={allExcluded || exclusionsLocked}
                    aria-label={t(
                      allTimingExcluded ? "includeTiming" : "excludeTiming"
                    )}
                    sx={{ p: 0.25 }}
                  >
                    <BrowseGalleryIcon
                      sx={{
                        fontSize: "1.2rem",
                        opacity: allTimingExcluded ? 0.3 : 1,
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  exclusionsLocked
                    ? lockedTitle
                    : t(
                        allMaterialExcluded
                          ? "includeMaterials"
                          : "excludeMaterials"
                      )
                }
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    onClick={() => {
                      toggleMaterialExclusion(allPeriodIds);
                    }}
                    disabled={allExcluded || exclusionsLocked}
                    aria-label={t(
                      allMaterialExcluded
                        ? "includeMaterials"
                        : "excludeMaterials"
                    )}
                    sx={{ p: 0.25 }}
                  >
                    <CategoryIcon
                      sx={{
                        fontSize: "1.2rem",
                        opacity: allMaterialExcluded ? 0.3 : 1,
                      }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
              {visibilityButton}
            </Stack>
          );

          return (
            <LabellerTimelineRow
              key={laneKey(lane)}
              labellerDetails={lane.labellerDetails}
              periods={lane.periods}
              visibleStart={visibleStart}
              visibleDuration={visibleDuration}
              endAdornment={endAdornment}
              currentTime={currentTime}
            />
          );
        })}
      </Stack>
    </Box>
  );
};
